import React, { useState, useEffect, useRef, useCallback } from 'react';
import "./TestimonialCarousel.css";

interface TestimonialCarouselProps {
    testimonials: string[];
    autoScrollInterval?: number;
}

const fiveStars = '/images/five-stars.webp';

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({ 
    testimonials, 
    autoScrollInterval = 5000
}) => {
    const [currentIndex, setCurrentIndex] = useState(1); // Start at 1 to accommodate clone
    const [isTransitioning, setIsTransitioning] = useState(true);
    const [isNavigating, setIsNavigating] = useState(false); // Prevent rapid clicks
    const carouselRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<NodeJS.Timeout>(null);

    const startAutoScroll = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setIsTransitioning(true);
            setCurrentIndex(prev => prev + 1);
        }, autoScrollInterval);
    }, [autoScrollInterval]);

    useEffect(() => {
        startAutoScroll();
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [startAutoScroll]);

    // Reset carousel when page becomes visible again
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && testimonials && testimonials.length > 0) {
                // Page became visible, reset to a safe state
                setIsTransitioning(false);
                setCurrentIndex(1); // Reset to first real testimonial
                setIsNavigating(false);
                
                // Re-enable transitions after a brief delay
                setTimeout(() => {
                    setIsTransitioning(true);
                    startAutoScroll();
                }, 100);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [testimonials, startAutoScroll]);

    const handleNavigation = (direction: 'prev' | 'next') => {
        // Prevent rapid clicking during transitions
        if (isNavigating) return;
        
        setIsNavigating(true);
        setIsTransitioning(true);
        
        if (direction === 'next') {
            setCurrentIndex(prev => prev + 1);
        } else {
            setCurrentIndex(prev => prev - 1);
        }
        startAutoScroll();
        
        // Reset navigation lock after transition completes
        setTimeout(() => {
            setIsNavigating(false);
        }, 350); 
    };

    useEffect(() => {
        const handleTransitionEnd = () => {
            if (!testimonials || testimonials.length === 0) return;
            
            if (currentIndex === testimonials.length + 1) {
                // At the clone of first item, jump to real first item
                setIsTransitioning(false);
                setCurrentIndex(1);
            } else if (currentIndex === 0) {
                // At the clone of last item, jump to real last item
                setIsTransitioning(false);
                setCurrentIndex(testimonials.length);
            }
            
            // Re-enable transitions after instant jump
            setTimeout(() => {
                setIsTransitioning(true);
            }, 50);
        };
        
        // Safety check: if index is out of bounds, reset
        if (testimonials && testimonials.length > 0) {
            if (currentIndex < 0 || currentIndex > testimonials.length + 1) {
                setIsTransitioning(false);
                setCurrentIndex(1);
                setTimeout(() => {
                    setIsTransitioning(true);
                }, 50);
            }
        }
        
        const carousel = carouselRef.current;
        if (carousel) {
            carousel.addEventListener('transitionend', handleTransitionEnd);
        }
        return () => {
            if (carousel) {
                carousel.removeEventListener('transitionend', handleTransitionEnd);
            }
        };
    }, [currentIndex, testimonials]);

    // Add safety check for empty testimonials AFTER all hooks
    if (!testimonials || testimonials.length === 0) {
        return (
            <div className="testimonial-carousel-container">
                <div className="testimonial-slide">
                    <blockquote>
                        <p>No testimonials available.</p>
                    </blockquote>
                </div>
            </div>
        );
    }

    return (
        <div className="testimonial-carousel-container">
            <button 
                className={`carousel-button prev ${isNavigating ? 'disabled' : ''}`}
                onClick={() => handleNavigation('prev')}
                disabled={isNavigating}
            >
                ‹
            </button>
            
            <div 
                ref={carouselRef}
                className="testimonial-carousel"
                style={{
                    transform: `translateX(-${currentIndex * 100}%)`,
                    transition: isTransitioning ? 'transform 0.5s ease' : 'none'
                }}
            >
                {/* Clone of last testimonial for smooth backward transition */}
                <div className="testimonial-slide">
                    <blockquote>
                        <img
                            src={fiveStars}
                            alt="5 stars"
                            className="testimonial-stars"
                        />
                        <p>{testimonials[testimonials.length - 1]}</p>
                    </blockquote>
                </div>
                
                {/* Original testimonials */}
                {testimonials.map((testimonial, index) => (
                    <div key={index} className="testimonial-slide">
                        <blockquote>
                            <img
                                src={fiveStars}
                                alt="5 stars"
                                className="testimonial-stars"
                            />
                            <p>{testimonial}</p>
                        </blockquote>
                    </div>
                ))}
                
                {/* Clone of first testimonial for smooth forward transition */}
                <div className="testimonial-slide">
                    <blockquote>
                        <img
                            src={fiveStars}
                            alt="5 stars"
                            className="testimonial-stars"
                        />
                        <p>{testimonials[0]}</p>
                    </blockquote>
                </div>
            </div>

            <button 
                className={`carousel-button next ${isNavigating ? 'disabled' : ''}`}
                onClick={() => handleNavigation('next')}
                disabled={isNavigating}
            >
                ›
            </button>
        </div>
    );
};

export default TestimonialCarousel;